import type { TeamMemberEntity } from '@/api/entity_types/TeamMember';
import { ref } from 'vue';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { useTeamStore } from '@/stores/team';

type Option = {
  name: string;
  payload: { assign_to_role: 'agent' } | { other_user_id: number; assign_to_role: 'other' };
};

export const useTaskAssigneeOptions = () => {
  const teamStore = useTeamStore();
  const currentUserStore = useCurrentUserStore();
  const loggedInUsername = currentUserStore.currentUserAccountDetails.username;
  const assigneeBind = ref<string>('');
  const options = ref<Option[]>([]);

  const assigneeOptions = () => {
    return options.value.map(option => option.name);
  };

  const selectedOption = () => {
    return options.value.find(option => option.name === assigneeBind.value)!;
  };

  const initializeAssignee = () => {
    assigneeBind.value = options.value[0]?.name || '';
  };

  options.value = buildOptions(teamStore.activeTeamMembers, loggedInUsername);
  initializeAssignee();

  return { assigneeBind, assigneeOptions, selectedOption, initializeAssignee };
};

const buildOptions = (teamMembers: TeamMemberEntity[], loggedInUsername: string): Option[] => {
  const agentOption: Option = { name: 'Assign to agent', payload: { assign_to_role: 'agent' } };

  const loggedInUserOption: Option = teamMembers
    .filter(teamMember => teamMember.email === loggedInUsername)
    .map(teamMember => ({
      payload: { other_user_id: teamMember.id, assign_to_role: 'other' },
      name: 'Assign to me',
    }))[0] as Option;

  const teamMemberOptions: Option[] = teamMembers
    .filter(teamMember => teamMember.email !== loggedInUsername)
    .map(teamMember => ({
      payload: { other_user_id: teamMember.id, assign_to_role: 'other' },
      name: `${teamMember.name} (${teamMember.email})`,
    }));

  return [loggedInUserOption, agentOption, ...teamMemberOptions].filter(option => option !== undefined);
};
